import SText from "@/styles/SText";
import { InspectionOrder, InspectionOrderItem, InspectionOrderStatus, InspectionProgress } from "@/types/inspection";
import { Button, Flex, Input, Modal, notification, Progress, Spin, Tooltip, Typography } from "antd";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useInspactionEnum from "../../hooks/useInspactionEnum";
import { itemUpdate, orderDel, orderFetch, orderFinish, orderProgress as orderProgressApi, orderUpdate } from "@/apis/inspection";
import ItemList from "./ItemList";
import CustomTabPane, { TabProps } from "@/components/TabPane";
import SDoc from "@/components/SDoc";
import { InspectionOrderContext } from "./context";
import PrintInspection from "./PrintInspection";
import { SConModal } from "@/styles/SConModal";

interface InspectionDetailProps {
  open: boolean;
  detail: InspectionOrder;
  onClose: () => void;
  onUpdate: (data: InspectionOrder) => void;
};

const InspectionDetail: FC<InspectionDetailProps> = ({ open, detail, onClose, onUpdate }) => {
  const { t } = useTranslation();
  const { getInspectionStatus } = useInspactionEnum();

  const [orderData, setOrderData] = useState<InspectionOrder>({ ...detail });
  const [items, setItems] = useState<InspectionOrderItem[]>([]);

  const [progress, setProgress] = useState<InspectionProgress | null>(null);
  const [desc, setDesc] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [cancelRow, setCancelRow] = useState<"C" | "F" | null>(null);

  const info = getInspectionStatus(orderData.status);

  useEffect(() => {
    detail.id && initData(detail.id);
  }, [detail]);

  const initData = async (id: string) => {
    setLoading(true);
    let order = await orderFetch(id);
    setOrderData({ ...order });
    !!order.items && setItems([...order.items]);
    setDesc(order.remark);
    let orderProgress = await orderProgressApi(id);
    setProgress({ ...orderProgress });
    setLoading(false);
  };


  const saveDesc = () => {
    setLoading(true);
    orderUpdate(detail.id, { remark: desc })
      .then(() => {
        setLoading(false);
        setOrderData(last => ({ ...last, remark: desc }));
        onUpdate({ ...orderData, remark: desc });
        notification.success({ message: t('inspection.saveOk') });
      })
      .catch((error) => {
        setLoading(false);
        notification.error({ message: t('inspection.saveErr') });
      })
  };

  const handleItemDoc = (info: InspectionOrderItem, docId: string) => {
    setItems(last => last.map(item => ({ ...item, docId: item.id === info.id ? docId : item.docId })));
    itemUpdate(info.id, { docId });
  };

  const openTab: (info: InspectionOrderItem) => { ["doc"]: TabProps } = useCallback((info) => {
    return {
      doc: {
        key: "doc",
        label: `${info.locationCode}-${t('inspection.attechment')}`,
        children: <SDoc docId={info?.docId || undefined} handleNewDocId={(docId) => handleItemDoc(info, docId)} isRead={[InspectionOrderStatus.REJECTED, InspectionOrderStatus.RESOLVED].includes(orderData.status)} />
      },
    }
  }, [handleItemDoc]);

  const handleCancel = async (type: "C" | "F") => {
    try {
      setCancelRow(type);
      if (type === "C") {
        await orderDel(orderData.id);
        notification.success({ message: t('inspection.rejected'), description: orderData.number });
        setOrderData({
          ...orderData,
          status: InspectionOrderStatus.REJECTED
        });
        onUpdate({
          ...orderData,
          status: InspectionOrderStatus.REJECTED
        });
      }
      if (type === "F") {
        await orderFinish(orderData.id);
        notification.success({ message: t('inspection.finishOk'), description: orderData.number });
        setOrderData({
          ...orderData,
          status: InspectionOrderStatus.RESOLVED
        });
        onUpdate({
          ...orderData,
          status: InspectionOrderStatus.RESOLVED
        });
      }
    } catch (error) {
      let desc = '';
      if (error === 74310) desc = t('inspection.unFinish')
      if (type === "C") {
        notification.error({ message: t('inspection.cancelErr') });
      }
      if (type === "F") {
        notification.error({ message: t('inspection.finishErr'), description: desc });
      }
    } finally { setCancelRow(null) };
  };

  const DetailTitle = (
    <Flex gap={8} align="center" style={{ width: "100%" }}>
      <Typography.Title level={4} style={{ margin: 0 }}>{detail.number}</Typography.Title>
      <Flex align="center" gap={8} style={{ flex: 1, marginRight: 40 }}>
        {info && <SText tagMode text={info.label} color={info.color} tagStyle={{ width: "auto" }} />}
        {progress && (
          <Tooltip title={t('inspection.inspectProgress')}>
            <Progress style={{ paddingTop: 6 }}
              percent={(progress.inspected / progress.total) * 100}
              format={() => `${progress.inspected}/${progress.total}`}
              size={{ height: 16 }} status={progress.status === InspectionOrderStatus.REJECTED ? "exception" : undefined}
            />
          </Tooltip>
        )}
        {[InspectionOrderStatus.DRAFT, InspectionOrderStatus.PENDING].includes(orderData.status) && <Fragment>
          <SConModal title={t('common.tip')} content={t('inspection.cancelTip')} disable={cancelRow === "C"} onOk={() => handleCancel("C")} >
            <Button type="link" disabled={cancelRow === "C"} danger>{t('inspection.cancel')}</Button>
          </SConModal>
          <SConModal title={t('common.tip')} content={t('inspection.finishTip')} disable={cancelRow === "F"} onOk={() => handleCancel("F")} >
            <Button type="primary" disabled={cancelRow === "F"} >{t('inspection.finish')}</Button>
          </SConModal>
        </Fragment>}
        <PrintInspection inspectionOrder={orderData} />
      </Flex>
    </Flex>
  )
  return (
    <Modal title={DetailTitle} open={open} width={1200} onCancel={onClose} footer={null}>
      <Spin spinning={loading}>
        <InspectionOrderContext.Provider value={{ order: orderData, items, progress, setItems, setProgress }}>
          <Flex vertical style={{ height: "70vh" }}>
            <Flex align="baseline" gap={8} style={{ marginBottom: 8 }}>
              <SText style={{ width: "unset" }} text={t('inspection.remark') + ": "} />
              <Input value={desc}
                onChange={e => setDesc(e.target.value)} style={{ flex: 1 }} readOnly />
              {/* <Button disabled={desc === orderData.remark} onClick={() => saveDesc()}>{t('common.save')}</Button> */}
            </Flex>
            <CustomTabPane<InspectionOrderItem> defaultKey={"item"} defaultTabs={[
              {
                key: "item",
                label: t("inspection.inspectItem"),
                children: <ItemList />,
                closable: false,
              },
            ]} onOpens={(info) => openTab(info)} />
          </Flex>
        </InspectionOrderContext.Provider>
      </Spin>
    </Modal>
  )
};

export default InspectionDetail;